import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCardSliderRefs
 * @property {HTMLElement} track - The slider track containing all images.
 * @property {HTMLElement[]} slides - The individual slide elements.
 * @property {HTMLButtonElement} prevButton - The previous navigation button.
 * @property {HTMLButtonElement} nextButton - The next navigation button.
 * @property {HTMLElement} indicator - The position indicator element.
 */

/**
 * Product card slider component that activates on hover.
 * Supports drag navigation and click controls.
 *
 * @extends Component<ProductCardSliderRefs>
 */
class ProductCardSlider extends Component {
  #currentIndex = 0;
  #isDragging = false;
  #startX = 0;
  #currentX = 0;
  #isActive = false;
  #totalSlides = 0;
  #dragThreshold = 5;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  #init() {
    const { slides } = this.refs;
    if (!slides || slides.length <= 1) {
      this.#hideControls();
      return;
    }

    this.#totalSlides = slides.length;
    this.#updateIndicator();
    this.#updateButtonStates();

    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
    window.addEventListener('resize', this.#handleResize);

    this.#setupDragListeners();
    this.#goToSlide(this.#currentIndex);
  }

  #hideControls() {
    const controls = this.querySelector('.product-card-slider__controls');
    if (controls) {
      /** @type {HTMLElement} */ (controls).style.display = 'none';
    }
  }

  #handleMouseEnter = () => {
    this.#activate();
  };

  #handleMouseLeave = () => {
    this.#isActive = false;
    this.classList.remove('is-active');
    if (this.#isDragging) {
      this.#finishDrag();
    } else {
      this.#goToSlide(this.#currentIndex);
    }
  };

  #setupDragListeners() {
    if (this.hasAttribute('data-drag-disabled')) return;

    const { track } = this.refs;
    if (!track) return;

    track.addEventListener('mousedown', this.#handleDragStart);
    track.addEventListener('touchstart', this.#handleDragStart, { passive: true });

    document.addEventListener('mousemove', this.#handleDragMove);
    document.addEventListener('touchmove', this.#handleDragMove, { passive: false });

    document.addEventListener('mouseup', this.#handleDragEnd);
    document.addEventListener('touchend', this.#handleDragEnd);
  }

  /**
   * @param {MouseEvent | TouchEvent} e
   */
  #handleDragStart = (e) => {
    if (!this.#isActive) {
      this.#activate();
    }

    this.#startX = this.#getClientX(e);
    this.#currentX = 0;
  };

  /**
   * @param {MouseEvent | TouchEvent} e
   */
  #handleDragMove = (e) => {
    if (!this.#isActive || this.#startX === 0) return;

    const clientX = this.#getClientX(e);
    this.#currentX = clientX - this.#startX;

    if (!this.#isDragging && Math.abs(this.#currentX) > this.#dragThreshold) {
      this.#isDragging = true;
      const { track } = this.refs;
      if (track) {
        track.style.cursor = 'grabbing';
      }
    }

    if (this.#isDragging) {
      const offset = -this.#getSlideOffset(this.#currentIndex) + this.#currentX;
      this.#setTrackPosition(offset);

      if (e.cancelable) {
        e.preventDefault();
      }
    }
  };

  #handleDragEnd = () => {
    if (this.#startX === 0) return;

    const { track } = this.refs;
    if (track) {
      track.style.cursor = '';
    }

    if (!this.#isDragging && Math.abs(this.#currentX) <= this.#dragThreshold) {
      const productUrl = this.getAttribute('data-product-url');
      if (productUrl) {
        window.location.href = productUrl;
        return;
      }
    }

    if (this.#isDragging) {
      this.#finishDrag();
    }

    this.#startX = 0;
    this.#currentX = 0;
  };

  #finishDrag() {
    if (!this.#isDragging) return;

    const { track } = this.refs;
    if (track) {
      track.style.cursor = '';
    }

    const threshold = this.#getSlideWidth(this.#currentIndex) * 0.2;

    if (this.#currentX < -threshold) {
      this.#goToSlide(this.#currentIndex + 1);
    } else if (this.#currentX > threshold) {
      this.#goToSlide(this.#currentIndex - 1);
    } else {
      this.#goToSlide(this.#currentIndex);
    }

    this.#isDragging = false;
    this.#startX = 0;
    this.#currentX = 0;
  }

  /**
   * @param {MouseEvent | TouchEvent} e
   * @returns {number}
   */
  #getClientX(e) {
    if ('touches' in e) {
      return e.touches[0]?.clientX ?? 0;
    }
    return e.clientX;
  }

  /**
   * @param {number} offset
   */
  #setTrackPosition(offset) {
    const { track } = this.refs;
    if (!track) return;
    track.style.transform = `translateX(${offset}px)`;
  }

  /**
   * @param {number} index
   * @returns {number}
   */
  #getSlideOffset(index) {
    const { slides } = this.refs;
    const slide = slides?.[index];

    return slide?.offsetLeft ?? 0;
  }

  /**
   * @param {number} index
   * @returns {number}
   */
  #getSlideWidth(index) {
    const { slides } = this.refs;
    const slide = slides?.[index];

    return slide?.offsetWidth ?? this.offsetWidth;
  }

  #activate() {
    this.#isActive = true;
    this.classList.add('is-active');
    this.#goToSlide(this.#currentIndex);
  }

  #handleResize = () => {
    this.#goToSlide(this.#currentIndex);
  };

  /**
   * @param {number} index
   */
  #goToSlide(index) {
    this.#currentIndex = ((index % this.#totalSlides) + this.#totalSlides) % this.#totalSlides;

    if (this.#currentIndex === 0) {
      this.classList.add('is-first-slide');
    } else {
      this.classList.remove('is-first-slide');
    }
    const offset = this.#getSlideOffset(this.#currentIndex);

    const { track } = this.refs;
    if (track) {
      track.style.transition = 'transform 0.3s ease';
      this.#setTrackPosition(-offset);

      const handleTransitionEnd = () => {
        track.style.transition = '';
        track.removeEventListener('transitionend', handleTransitionEnd);
      };
      track.addEventListener('transitionend', handleTransitionEnd);
    }

    this.#updateIndicator();
    this.#updateButtonStates();
  }

  handlePrevClick() {
    this.#goToSlide(this.#currentIndex - 1);
  }

  handleNextClick() {
    this.#goToSlide(this.#currentIndex + 1);
  }

  #updateIndicator() {
    const { indicator } = this.refs;
    if (!indicator) return;
    indicator.textContent = `[${this.#currentIndex + 1}/${this.#totalSlides}]`;
  }

  #updateButtonStates() {
    const { prevButton, nextButton } = this.refs;
    if (prevButton) prevButton.disabled = false;
    if (nextButton) nextButton.disabled = false;
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    window.removeEventListener('resize', this.#handleResize);

    document.removeEventListener('mousemove', this.#handleDragMove);
    document.removeEventListener('touchmove', this.#handleDragMove);
    document.removeEventListener('mouseup', this.#handleDragEnd);
    document.removeEventListener('touchend', this.#handleDragEnd);

    super.disconnectedCallback();
  }
}

if (!customElements.get('product-card-slider')) {
  customElements.define('product-card-slider', ProductCardSlider);
}
